import { motion } from 'framer-motion';
import React, { useState } from 'react';

const TermsAndPolicies = () => {
  const [activeSection, setActiveSection] = useState('terms');

  const sections = [
    { id: 'terms', title: 'Terms of Service' },
    { id: 'privacy', title: 'Privacy Policy' },
    { id: 'refund', title: 'Refund Policy' },
    { id: 'shipping', title: 'Shipping Policy' },
    { id: 'cancellation', title: 'Cancellation Policy' },
  ];

  const content = {
    terms: {
      title: 'Terms of Service',
      sections: [
        {
          heading: '1. Acceptance of Terms',
          text: 'By accessing and using Kigo Prints website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.',
        },
        {
          heading: '2. Use License',
          text: 'Permission is granted to temporarily access the materials on Kigo Prints website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not: modify or copy the materials; use the materials for any commercial purpose or for any public display; attempt to reverse engineer any software contained on the website; remove any copyright or other proprietary notations from the materials.',
        },
        {
          heading: '3. User Accounts',
          text: 'You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account or password. You must notify us immediately of any unauthorized use of your account.',
        },
        {
          heading: '4. Product Information',
          text: 'We strive to provide accurate product descriptions and images. However, we do not warrant that product descriptions or other content on this site is accurate, complete, reliable, current, or error-free. Colors may vary slightly from what is displayed on your monitor.',
        },
        {
          heading: '5. Pricing and Payment',
          text: 'All prices are listed in Indian Rupees (INR) unless otherwise stated. We reserve the right to change prices at any time without prior notice. Payment must be made at the time of order placement. We accept various payment methods including credit/debit cards, UPI, and other digital payment options.',
        },
        {
          heading: '6. Intellectual Property',
          text: 'All content on this website, including but not limited to text, graphics, logos, images, and software, is the property of Kigo Prints and is protected by copyright and other intellectual property laws.',
        },
        {
          heading: '7. Limitation of Liability',
          text: 'Kigo Prints shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.',
        },
      ],
    },
    privacy: {
      title: 'Privacy Policy',
      sections: [
        {
          heading: '1. Information We Collect',
          text: 'We collect information that you provide directly to us, including name, email address, phone number, shipping address, payment information, and any other information you choose to provide when using our services.',
        },
        {
          heading: '2. How We Use Your Information',
          text: 'We use the information we collect to process your orders, communicate with you about your orders, send you marketing communications (with your consent), improve our services, and comply with legal obligations.',
        },
        {
          heading: '3. Information Sharing',
          text: 'We do not sell, trade, or rent your personal information to third parties. We may share your information with service providers who assist us in operating our website, conducting our business, or serving our users, as long as those parties agree to keep this information confidential.',
        },
        {
          heading: '4. Data Security',
          text: 'We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.',
        },
        {
          heading: '5. Cookies',
          text: 'We use cookies to enhance your experience on our website. You can choose to disable cookies through your browser settings, though this may affect the functionality of our website.',
        },
        {
          heading: '6. Your Rights',
          text: 'You have the right to access, update, or delete your personal information at any time. You can also opt-out of receiving marketing communications from us.',
        },
        {
          heading: '7. Changes to Privacy Policy',
          text: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.',
        },
      ],
    },
    refund: {
      title: 'Refund Policy',
      sections: [
        {
          heading: '1. Eligibility for Refunds',
          text: 'We offer refunds or replacements for products that are defective, damaged during shipping, or do not match the description provided. Refund requests must be made within 7 days of receiving the product.',
        },
        {
          heading: '2. Refund Process',
          text: 'To request a refund, please contact our customer support team with your order number and details of the issue. We will review your request and respond within 2-3 business days. If approved, refunds will be processed to the original payment method within 5-10 business days.',
        },
        {
          heading: '3. Non-Refundable Items',
          text: 'Customized products that have been printed according to your specifications and are not defective are generally not eligible for refunds. Digital products and services are non-refundable once delivered.',
        },
        {
          heading: '4. Return Shipping',
          text: 'If a return is required, we will provide you with a return shipping label. Products must be returned in their original condition with all packaging materials.',
        },
        {
          heading: '5. Partial Refunds',
          text: 'In some cases, we may offer partial refunds if only a portion of your order is defective or incorrect. This will be determined on a case-by-case basis.',
        },
        {
          heading: '6. Refund for Cancelled Orders',
          text: 'If you cancel your order before it enters production, you will receive a full refund. If the order has already entered production, cancellation may not be possible, and refund eligibility will be determined based on the production stage.',
        },
      ],
    },
    shipping: {
      title: 'Shipping Policy',
      sections: [
        {
          heading: '1. Shipping Locations',
          text: 'We currently ship to addresses within India. International shipping may be available for select products - please contact us for more information.',
        },
        {
          heading: '2. Shipping Methods',
          text: 'We offer various shipping options including standard shipping, express shipping, and same-day delivery (where available). Shipping costs and delivery times vary based on the selected method and destination.',
        },
        {
          heading: '3. Processing Time',
          text: 'Orders are typically processed within 1-3 business days. Custom products may require additional processing time. Production time will be clearly indicated during checkout.',
        },
        {
          heading: '4. Delivery Time',
          text: 'Standard shipping typically takes 3-7 business days, while express shipping takes 1-3 business days. Delivery times are estimates and may vary based on location and external factors.',
        },
        {
          heading: '5. Shipping Costs',
          text: 'Shipping costs are calculated based on the weight, dimensions, and destination of your order. Free shipping may be available for orders above a certain value - check our website for current promotions.',
        },
        {
          heading: '6. Order Tracking',
          text: 'Once your order ships, you will receive a tracking number via email. You can use this number to track your package on the courier\'s website.',
        },
        {
          heading: '7. Delivery Issues',
          text: 'If you experience any issues with delivery, such as damaged packages or incorrect addresses, please contact our customer support team immediately. We will work with you to resolve the issue.',
        },
        {
          heading: '8. Undeliverable Packages',
          text: 'If a package is returned to us due to an incorrect address or failure to collect, we will contact you to arrange reshipment. Additional shipping charges may apply.',
        },
      ],
    },
    cancellation: {
      title: 'Cancellation Policy',
      sections: [
        {
          heading: '1. Cancellation Window',
          text: 'You may cancel your order within 24 hours of placement for a full refund, provided the order has not entered production. For orders that have entered production, cancellation may not be possible.',
        },
        {
          heading: '2. How to Cancel',
          text: 'To cancel an order, please contact our customer support team via phone or email with your order number. We will process your cancellation request and confirm via email.',
        },
        {
          heading: '3. Cancellation Fees',
          text: 'Cancellations made within the allowed window are free of charge. If an order has already entered production, cancellation may incur fees based on the work completed. These fees will be clearly communicated before processing the cancellation.',
        },
        {
          heading: '4. Refund for Cancelled Orders',
          text: 'If your cancellation is approved, you will receive a full refund to your original payment method within 5-10 business days. The refund amount will exclude any non-refundable fees if applicable.',
        },
        {
          heading: '5. Custom Orders',
          text: 'Custom orders that have been personalized according to your specifications cannot be cancelled once production begins, as they cannot be resold.',
        },
        {
          heading: '6. Bulk Orders',
          text: 'For bulk orders, cancellation policies may vary. Please contact us before placing a bulk order to discuss cancellation terms specific to your order.',
        },
      ],
    },
  };

  return (
    <div className="bg-[#f3f5f3] min-h-screen" style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}>
      <section className="py-8 sm:py-12 lg:py-16 xl:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-8 sm:mb-12 lg:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-4">
                Terms & Policies
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600">
                Please read our terms and policies carefully
              </p>
            </motion.div>

            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              <motion.div
                className="lg:w-1/4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 sticky top-4">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Quick Navigation</h2>
                  <nav className="space-y-2">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full text-left px-4 py-2.5 sm:py-3 rounded-2xl transition-all duration-200 text-sm sm:text-base ${activeSection === section.id
                          ? 'bg-[#008000] text-white font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </motion.div>

              <motion.div
                className="lg:w-3/4"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 mb-6 sm:mb-8">
                    {content[activeSection].title}
                  </h2>
                  <div className="space-y-6 sm:space-y-8">
                    {content[activeSection].sections.map((section, index) => (
                      <motion.div
                        key={index}
                        className="p-4 sm:p-5 lg:p-6 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                        <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                          {section.heading}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                          {section.text}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              className="mt-8 sm:mt-12 lg:mt-16 bg-white rounded-xl shadow-sm p-6 sm:p-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                Last Updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                If you have any questions about our terms and policies, please contact us.
              </p>
              <motion.a
                href="tel:+918080184435"
                className="inline-block px-6 sm:px-8 py-2.5 sm:py-3 bg-[#008000] text-white rounded-xl hover:bg-[#008000] transition-all duration-300 text-sm sm:text-base font-medium shadow-md"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                whileTap={{ scale: 0.98 }}
              >
                Contact Support
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndPolicies;

